import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import type { ReactNode } from 'react';

import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/cn';

// Phase 8.5-05 — DataTable extension (ADR-014). Adds three optional
// server-side props that are wired together when present:
// - `pagination` (page/totalPages/onPageChange) renders a `<Pagination>`
//   bar in the footer. `total === undefined` falls back to client-side
//   (no bar rendered).
// - `sort` + `onSortChange` enables clickable sort headers. Columns must
//   declare `meta: { sortField: '<name>' }` to participate; non-sortable
//   columns render plain text. Click cycle is `null → asc → desc → null`
//   (3-state — null returns to the route's defaultSort).
// - `filterSlot` slots above the table for filter controls (status
//   `<Select>`, etc).
//
// Existing client-side callers (warehouses/users) are unchanged — every
// new prop is optional and unused defaults preserve the original render.

export type SortDir = 'asc' | 'desc';

export interface DataTableSortState {
  field: string | null;
  dir: SortDir;
}

export interface DataTableServerSidePagination {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  emptyMessage?: string;
  onRowClick?: (row: TData) => void;
  footer?: ReactNode;
  filterSlot?: ReactNode;
  pagination?: DataTableServerSidePagination;
  sort?: DataTableSortState;
  onSortChange?: (next: DataTableSortState) => void;
}

const cycleSort = (current: DataTableSortState | undefined, field: string): DataTableSortState => {
  if (current?.field !== field) return { field, dir: 'asc' };
  if (current.dir === 'asc') return { field, dir: 'desc' };
  // current.dir === 'desc' → reset to default (null field).
  return { field: null, dir: 'desc' };
};

const SortIcon = ({ state, field }: { state: DataTableSortState | undefined; field: string }) => {
  if (state?.field !== field) {
    return <ArrowUpDown className="ml-1 inline h-3.5 w-3.5 opacity-50" aria-hidden />;
  }
  return state.dir === 'asc' ? (
    <ArrowUp className="ml-1 inline h-3.5 w-3.5" aria-hidden />
  ) : (
    <ArrowDown className="ml-1 inline h-3.5 w-3.5" aria-hidden />
  );
};

export const DataTable = <TData, TValue>({
  columns,
  data,
  emptyMessage = 'Нет данных',
  onRowClick,
  footer,
  filterSlot,
  pagination,
  sort,
  onSortChange,
}: DataTableProps<TData, TValue>) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalPages =
    pagination && pagination.pageSize > 0
      ? Math.max(0, Math.ceil(pagination.total / pagination.pageSize))
      : 0;

  return (
    <div className="space-y-3">
      {filterSlot ? <div className="flex items-center gap-3">{filterSlot}</div> : null}
      <div>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((group) => (
              <TableRow key={group.id}>
                {group.headers.map((header) => {
                  if (header.isPlaceholder) {
                    return <TableHead key={header.id} />;
                  }
                  // ColumnDef.meta is typed `unknown`; opt into a narrow
                  // `sortField` slot without polluting the @tanstack types.
                  const meta = header.column.columnDef.meta as { sortField?: string } | undefined;
                  const sortField = meta?.sortField;
                  if (!sortField || !onSortChange) {
                    return (
                      <TableHead key={header.id}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    );
                  }
                  return (
                    <TableHead key={header.id}>
                      <button
                        type="button"
                        className="-ml-2 inline-flex items-center rounded px-2 py-1 hover:bg-accent hover:text-accent-foreground"
                        onClick={() => {
                          onSortChange(cycleSort(sort, sortField));
                        }}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <SortIcon state={sort} field={sortField} />
                      </button>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={
                    onRowClick
                      ? () => {
                          onRowClick(row.original);
                        }
                      : undefined
                  }
                  className={cn(onRowClick && 'cursor-pointer')}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {pagination ? (
          <div className="border-t border-border p-4">
            <Pagination
              page={pagination.page}
              totalPages={totalPages}
              onPageChange={pagination.onPageChange}
            />
          </div>
        ) : null}
        {footer ? <div className="border-t border-border p-4">{footer}</div> : null}
      </div>
    </div>
  );
};
